import clsx from 'clsx';
import { useFormik } from 'formik';
import { FC, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { ShowError } from '../../../../errors/components/ShowError';
import { UsersListLoading } from '../../../user-management/users-list/components/loading/UsersListLoading';
import { ThemeModalState } from '../../themes-grid/model/types';
import { ThemePreview } from './ThemePreview';
import {
	useCreateTheme,
	useDeleteTheme,
	useUpdateTheme,
} from '../api/themes.mutations';

type ColorKey =
	| 'background'
	| 'surface'
	| 'surface_muted'
	| 'primary'
	| 'primary_foreground'
	| 'text'
	| 'text_muted'
	| 'border'
	| 'input'
	| 'input_border'
	| 'badge'
	| 'badge_text'
	| 'danger'
	| 'success';

const COLOR_FIELDS: { key: ColorKey; label: string }[] = [
	{ key: 'background', label: 'Фон' },
	{ key: 'surface', label: 'Поверхность' },
	{ key: 'surface_muted', label: 'Поверхность (приглушённая)' },
	{ key: 'primary', label: 'Основной цвет' },
	{ key: 'primary_foreground', label: 'Текст на основном' },
	{ key: 'text', label: 'Текст' },
	{ key: 'text_muted', label: 'Текст (приглушённый)' },
	{ key: 'border', label: 'Рамка' },
	{ key: 'input', label: 'Фон инпута' },
	{ key: 'input_border', label: 'Граница инпута' },
	{ key: 'badge', label: 'Бейдж' },
	{ key: 'badge_text', label: 'Текст бейджа' },
	{ key: 'danger', label: 'Ошибка' },
	{ key: 'success', label: 'Успех' },
];

const emptyColors: Record<ColorKey, string> = {
	background: '#ffffff',
	surface: '#f8f9fa',
	surface_muted: '#e9ecef',
	primary: '#3699ff',
	primary_foreground: '#ffffff',
	text: '#181c32',
	text_muted: '#a1a5b7',
	border: '#e4e6ef',
	input: '#f5f8fa',
	input_border: '#e4e6ef',
	badge: '#3699ff',
	badge_text: '#ffffff',
	danger: '#f1416c',
	success: '#50cd89',
};

const themeSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, 'Минимум 2 символа')
		.max(100, 'Максимум 100 символов')
		.required('Введите название'),
	description: Yup.string()
		.min(2, 'Минимум 2 символа')
		.max(300, 'Максимум 300 символов')
		.required('Введите описание'),
});

interface Props {
	openModal: ThemeModalState;
	handleModalClose: () => void;
	setShowErrorToast: (state: boolean) => void;
}

const ThemeEditModalForm: FC<Props> = ({
	openModal,
	handleModalClose,
	setShowErrorToast,
}) => {
	const [error, setError] = useState<any>();
	const [colors, setColors] = useState<Record<ColorKey, string>>(emptyColors);
	const initialColors = useRef<Record<ColorKey, string>>(emptyColors);

	const { mutate: createTheme, isLoading: isCreating } = useCreateTheme();
	const { mutate: editTheme, isLoading: isEditing } = useUpdateTheme();
	const { mutateAsync: deleteTheme, isLoading: isDeleting } = useDeleteTheme();

	const isLoading = isCreating || isEditing || isDeleting;
	const isDelete = openModal.type === 'delete' && openModal.data;

	const formik = useFormik({
		initialValues: { name: '', description: '' },
		validationSchema: !isDelete ? themeSchema : null,
		onSubmit: async (values, { setSubmitting }) => {
			setSubmitting(true);
			try {
				if (openModal.type === 'create') {
					createTheme(
						{ ...values, ...colors },
						{ onSuccess: handleModalClose, onError: () => setShowErrorToast(true) }
					);
					return;
				}
				if (openModal.type === 'edit' && openModal.data) {
					editTheme(
						{ uuid: openModal.data.uuid, request: { ...values, ...colors } },
						{ onSuccess: handleModalClose, onError: () => setShowErrorToast(true) }
					);
					return;
				}
				if (openModal.type === 'delete' && openModal.data) {
					await deleteTheme(openModal.data.uuid);
					handleModalClose();
				}
			} catch (ex: any) {
				setError(ex);
			} finally {
				setSubmitting(false);
			}
		},
	});

	useEffect(() => {
		if (openModal.type === 'edit' && openModal.data) {
			formik.setValues({
				name: openModal.data.name || '',
				description: openModal.data.description || '',
			});
			const c: Record<ColorKey, string> = {} as any;
			COLOR_FIELDS.forEach(({ key }) => {
				c[key] = openModal.data![key] || emptyColors[key];
			});
			setColors(c);
			initialColors.current = c;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [openModal]);

	return (
		<>
			<form
				id='kt_modal_theme_form'
				className='form'
				onSubmit={formik.handleSubmit}
				noValidate
			>
				<div
					className='d-flex flex-column scroll-y me-n7 ps-2 pe-7'
					data-kt-scroll='true'
					data-kt-scroll-activate='{default: false, lg: true}'
					data-kt-scroll-max-height='auto'
					data-kt-scroll-dependencies='#kt_modal_add_user_header'
					data-kt-scroll-wrappers='#kt_modal_add_user_scroll'
					data-kt-scroll-offset='300px'
				>
					{openModal.type !== 'delete' && (
						<>
							{/* Name */}
							<div className='fv-row mb-5'>
								<label className='required fw-bold fs-7 mb-2'>Название</label>
								<input
									placeholder='Telegram Dark'
									{...formik.getFieldProps('name')}
									type='text'
									className={clsx(
										'form-control form-control-solid mb-0',
										{ 'is-invalid': formik.touched.name && formik.errors.name },
										{ 'is-valid': formik.touched.name && !formik.errors.name }
									)}
									autoComplete='off'
									disabled={formik.isSubmitting}
								/>
								{formik.touched.name && formik.errors.name && (
									<div className='fv-plugins-message-container'>
										<div className='fv-help-block'>
											<span role='alert'>{formik.errors.name}</span>
										</div>
									</div>
								)}
							</div>

							{/* Description */}
							<div className='fv-row mb-6'>
								<label className='required fw-bold fs-7 mb-2'>Описание</label>
								<input
									placeholder='Стандартная тёмная тема'
									{...formik.getFieldProps('description')}
									type='text'
									className={clsx(
										'form-control form-control-solid mb-0',
										{
											'is-invalid':
												formik.touched.description && formik.errors.description,
										},
										{
											'is-valid':
												formik.touched.description && !formik.errors.description,
										}
									)}
									autoComplete='off'
									disabled={formik.isSubmitting}
								/>
								{formik.touched.description && formik.errors.description && (
									<div className='fv-plugins-message-container'>
										<div className='fv-help-block'>
											<span role='alert'>{formik.errors.description}</span>
										</div>
									</div>
								)}
							</div>

							{/* Colors */}
							<div className='separator separator-dashed mb-5' />
							<div className='d-flex align-items-center justify-content-between mb-4'>
								<span className='fw-bold fs-7 text-uppercase text-muted ls-1'>
									Цвета
								</span>
								{openModal.type === 'edit' && (
									<button
										type='button'
										className='btn btn-sm btn-light btn-active-light-primary'
										onClick={() => setColors(initialColors.current)}
									>
										Сбросить
									</button>
								)}
							</div>

							{/* Превью перед палитрой: цвет выбирают, глядя на результат,
							    а не на четырнадцать шестнадцатеричных строк. Липкое,
							    чтобы не уезжало вверх, пока крутишь список цветов. */}
							<div
								style={{
									position: 'sticky',
									top: 0,
									zIndex: 2,
									paddingBottom: 12,
									background: '#fff',
								}}
							>
								<ThemePreview colors={colors} />
							</div>

							<div className='row g-4'>
								{COLOR_FIELDS.map(({ key, label }) => (
									<div className='col-6' key={key}>
										<label className='fw-semibold fs-8 text-gray-600 mb-1 d-block'>
											{label}
										</label>
										<div className='d-flex align-items-center gap-2'>
											<input
												type='color'
												value={colors[key]}
												onChange={e =>
													setColors(c => ({ ...c, [key]: e.target.value }))
												}
												style={{
													width: '32px',
													height: '32px',
													padding: '2px',
													border: '1px solid #e4e6ef',
													borderRadius: '6px',
													cursor: 'pointer',
													flexShrink: 0,
												}}
											/>
											<input
												type='text'
												value={colors[key]}
												onChange={e => {
													const v = e.target.value;
													setColors(c => ({
														...c,
														[key]: v && !v.startsWith('#') ? `#${v}` : v,
													}));
												}}
												placeholder='#000000'
												className='form-control form-control-solid form-control-sm'
											/>
										</div>
									</div>
								))}
							</div>
						</>
					)}

					{isDelete && openModal.data && (
						<div className='fs-6 text-gray-700'>
							Вы собираетесь удалить тему{' '}
							<strong>{openModal.data.name}</strong>.{' '}
							Пожалуйста, подтвердите своё действие.
						</div>
					)}

					{error && <ShowError error={error} />}
				</div>

				<div className='text-center mt-7'>
					<button
						type='reset'
						className='btn btn-light btn-sm me-4 min-w-125px'
						onClick={handleModalClose}
						disabled={formik.isSubmitting}
					>
						Отмена
					</button>
					<button
						type='submit'
						className={clsx(
							'btn btn-sm min-w-125px',
							openModal.type === 'delete' ? 'btn-danger' : 'btn-primary'
						)}
						disabled={formik.isSubmitting || isLoading}
					>
						<span className='indicator-label'>
							{openModal.type === 'create'
								? 'Создать'
								: openModal.type === 'edit'
								? 'Сохранить'
								: 'Удалить'}
						</span>
						{(formik.isSubmitting || isLoading) && (
							<span className='indicator-progress'>
								Подождите...{' '}
								<span className='spinner-border spinner-border-sm align-middle ms-2' />
							</span>
						)}
					</button>
				</div>
			</form>
			{(formik.isSubmitting || isLoading) && <UsersListLoading />}
		</>
	);
};

export { ThemeEditModalForm };
